import { supabase } from '../lib/supabase';
import { activityService } from './activityService';

/**
 * Error-surfacing pattern: see canonical doc-comment at the top of
 * src/services/clientService.js.
 *
 * createAudit and updateAudit invoke activityService.createActivity for
 * audit-trail logging. Those calls intentionally fall through on failure
 * (the audit succeeded; logging the activity is best-effort) but they
 * `await` the call and now read its `error` to log a console warning.
 * Failing to log the activity should NOT unwind the audit save.
 */
function surfaceError(scope, error) {
  console.error(`[auditService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const auditService = {
  async createAudit(auditData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('site_audits')?.insert({
          site_id: auditData?.siteId,
          company_id: auditData?.companyId,
          audit_name: auditData?.auditName,
          audit_date: auditData?.auditDate,
          auditor_name: auditData?.auditorName,
          status: auditData?.status || 'draft',
          overall_result: auditData?.overallResult || null,
          notes: auditData?.notes || null,
          conducted_by: user?.id
        })?.select()?.single();

      if (error) throw error;

      // Best-effort activity log — don't fail the audit save if logging fails.
      if (data?.site_id) {
        const logRes = await activityService?.createActivity({
          companyId: data?.company_id,
          siteId: data?.site_id,
          activityType: 'inspection_created',
          title: `Inspection created: ${data?.audit_name}`,
          description: `Auditor: ${data?.auditor_name || 'N/A'} | Date: ${data?.audit_date || 'N/A'}`,
          severityLevel: 'low',
          relatedEntityId: data?.id,
          metadata: { auditId: data?.id, auditName: data?.audit_name, auditorName: data?.auditor_name }
        });
        if (logRes?.error) {
          console.warn('[auditService] inspection_created activity log failed (non-fatal):', { auditId: data?.id, code: logRes?.error?.code, message: logRes?.error?.message });
        }
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('createAudit', error) };
    }
  },

  // NSherjFh PR 7: default-filters soft-deleted audits. Pass
  // `{ includeDeleted: true }` from the audits-inspections 'View
  // deleted' tab. Mirrors PR 2-6 opt-in shape exactly. status
  // (draft / in_progress / completed) and is_read_only are SEPARATE
  // axes from deleted_at — a completed audit is still active; a
  // soft-deleted audit is hidden regardless of status. is_read_only
  // is a UI-only badge (PR 5 finding) and doesn't interact here.
  async getAuditsBySite(siteId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_audits')
        ?.select('*')
        ?.eq('site_id', siteId)
        ?.order('audit_date', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAuditsBySite', error) };
    }
  },

  async getAllAudits(options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_audits')
        ?.select('*, sites(site_name)')
        ?.order('audit_date', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAllAudits', error) };
    }
  },

  async getAuditById(auditId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('site_audits')
        ?.select('*, sites(site_name)')
        ?.eq('id', auditId);
      if (!includeDeleted) query = query?.is('deleted_at', null);
      // .maybeSingle so a soft-deleted id resolves to null gracefully
      // (callers render not-found) instead of throwing PGRST116.
      const { data, error } = await query?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('getAuditById', error) };
    }
  },

  async updateAudit(auditId, updates) {
    try {
      const { data, error } = await supabase?.from('site_audits')?.update(updates)?.eq('id', auditId)?.select()?.single();

      if (error) throw error;

      if (updates?.status === 'completed' && data?.site_id) {
        const isPass = data?.overall_result === 'pass';
        const logRes = await activityService?.createActivity({
          companyId: data?.company_id,
          siteId: data?.site_id,
          activityType: 'inspection_completed',
          title: `Inspection completed: ${data?.audit_name}`,
          description: `Result: ${data?.overall_result || 'N/A'} | Auditor: ${data?.auditor_name || 'N/A'}`,
          severityLevel: isPass ? 'low' : 'high',
          relatedEntityId: data?.id,
          metadata: { auditId: data?.id, auditName: data?.audit_name, overallResult: data?.overall_result }
        });
        if (logRes?.error) {
          console.warn('[auditService] inspection_completed activity log failed (non-fatal):', { auditId: data?.id, code: logRes?.error?.code, message: logRes?.error?.message });
        }
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('updateAudit', error) };
    }
  },

  // NSherjFh PR 7 — recoverable soft-delete on the audit row.
  //
  // Built from scratch (no existing delete method or UI; existing
  // admins_sm_delete_site_audits RLS policy is preserved for a
  // future true-purge path). Stamps deleted_at + deleted_by;
  // idempotent via .is('deleted_at', null) + .maybeSingle.
  //
  // audit_questions + audit_responses + photo_path entries on
  // responses are preserved in the DB and cascade-hide via the
  // parent's deleted_at filter on every reader. Restoring brings
  // the whole audit back exactly as it was — saved progress,
  // status, overall_result, all responses intact.
  //
  // RLS: covered by the existing admins_sm_update_site_audits
  // policy (USING company_id + is_admin_or_site_manager(); no
  // deleted_at predicate, so restore round-trip works). UI gates
  // to admin via isAdmin even though the policy permits site_
  // manager — same pattern as PRs 4-6 (RLS may permit more than
  // the UI exposes).
  async deleteAudit(auditId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('site_audits')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', auditId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('deleteAudit', error) };
    }
  },

  // Inverse of deleteAudit. Clears deleted_at/_by so the audit
  // reappears across every active surface (audits-inspections list,
  // compliance dashboard, client portal). Child questions and
  // responses were never touched, so they re-surface automatically
  // when the parent unblocks.
  async restoreAudit(auditId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        ?.from('site_audits')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', auditId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('restoreAudit', error) };
    }
  },

  async addAuditQuestion(questionData) {
    try {
      const { data, error } = await supabase?.from('audit_questions')?.insert({
          audit_id: questionData?.auditId,
          question_text: questionData?.questionText,
          category: questionData?.category || null,
          display_order: questionData?.displayOrder || 0
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('addAuditQuestion', error) };
    }
  },

  async getAuditQuestions(auditId) {
    try {
      const { data, error } = await supabase?.from('audit_questions')?.select('*')?.eq('audit_id', auditId)?.order('display_order', { ascending: true });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAuditQuestions', error) };
    }
  },

  async addAuditResponse(responseData) {
    try {
      const { data, error } = await supabase?.from('audit_responses')?.insert({
          audit_id: responseData?.auditId,
          question_id: responseData?.questionId,
          pass: responseData?.pass,
          comment: responseData?.comment || null,
          photo_path: responseData?.photoPath || null
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('addAuditResponse', error) };
    }
  },

  async getAuditResponses(auditId) {
    try {
      const { data, error } = await supabase?.from('audit_responses')?.select('*, audit_questions(*)')?.eq('audit_id', auditId);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAuditResponses', error) };
    }
  }
};
